import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Mail, Phone, MapPin, Send } from "lucide-react";
import { usePageSeo, BASE_URL } from "@/hooks/useSeo";
import { Input } from "@/components/ui/input";
import Navigation from "@/components/Navigation";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function KontaktPage() {
  const [companyInfo, setCompanyInfo] = useState(null);
  const [formData, setFormData] = useState({ first_name: "", last_name: "", email: "", company: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/company-settings`).then(r => setCompanyInfo(r.data)).catch(() => {});
  }, []);

  usePageSeo({
    title: "Kontakt | LAB336",
    description: "Nehmen Sie Kontakt mit LAB336 auf. Wir beraten Sie zu Prozessoptimierung, Digitalisierung und KI-Lösungen für Ihr Unternehmen.",
    canonical: `${BASE_URL}/kontakt`,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Nachricht gesendet! Wir melden uns bei Ihnen.");
      setFormData({ first_name: "", last_name: "", email: "", company: "", message: "" });
    } catch {
      toast.error("Senden fehlgeschlagen. Bitte versuchen Sie es erneut.");
    }
    setSending(false);
  };

  const email = companyInfo?.email || "info@lab336.ch";
  const phone = companyInfo?.phone || "+41 79 664 08 15";
  const address = companyInfo?.address || "Schweiz";

  return (
    <div className="min-h-screen bg-white" data-testid="kontakt-page">
      <Navigation />
      <header className="bg-lab-bg-dark pt-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-12">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-lab-accent transition-colors text-sm mb-8" data-testid="back-to-home">
            <ArrowLeft className="w-4 h-4" /> Zurück zur Startseite
          </Link>
          <div>
            <h1 className="font-playfair text-3xl lg:text-4xl font-semibold text-white">Kontakt</h1>
            <p className="text-gray-400 mt-2">Lassen Sie uns über Ihr Projekt sprechen</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-5 gap-16">
          {/* Contact Info */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }} className="lg:col-span-2 space-y-8">
            <div>
              <h2 className="font-playfair text-xl font-semibold text-lab-primary mb-6">Direkter Kontakt</h2>
              <div className="space-y-5">
                <a href={`mailto:${email}`} className="flex items-center gap-4 text-lab-text hover:text-lab-accent transition-colors group" data-testid="kontakt-email">
                  <div className="w-12 h-12 bg-lab-bg-light border border-lab-border flex items-center justify-center group-hover:border-lab-accent transition-colors">
                    <Mail className="w-5 h-5 text-lab-accent" />
                  </div>
                  <span className="text-sm">{email}</span>
                </a>
                <a href={`tel:${phone.replace(/\s/g, "")}`} className="flex items-center gap-4 text-lab-text hover:text-lab-accent transition-colors group" data-testid="kontakt-phone">
                  <div className="w-12 h-12 bg-lab-bg-light border border-lab-border flex items-center justify-center group-hover:border-lab-accent transition-colors">
                    <Phone className="w-5 h-5 text-lab-accent" />
                  </div>
                  <span className="text-sm">{phone}</span>
                </a>
                <div className="flex items-center gap-4 text-lab-text">
                  <div className="w-12 h-12 bg-lab-bg-light border border-lab-border flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-lab-accent" />
                  </div>
                  <span className="text-sm">{address}</span>
                </div>
              </div>
            </div>

            <div className="p-6 bg-lab-bg-light border border-lab-border">
              <h3 className="font-semibold text-lab-primary text-sm mb-2">Kostenloses Erstgespräch</h3>
              <p className="text-lab-text-muted text-sm mb-4">Lieber direkt sprechen? Buchen Sie einen Termin.</p>
              <Link to="/termin" className="text-lab-accent text-sm font-medium hover:underline" data-testid="kontakt-termin-link">
                Termin buchen →
              </Link>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="lg:col-span-3">
            <h2 className="font-playfair text-xl font-semibold text-lab-primary mb-6">Nachricht senden</h2>
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="kontakt-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm text-lab-primary">Vorname *</Label>
                  <Input value={formData.first_name} onChange={e => setFormData({...formData, first_name: e.target.value})} required className="rounded-none border-lab-border" data-testid="kontakt-vorname" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm text-lab-primary">Nachname *</Label>
                  <Input value={formData.last_name} onChange={e => setFormData({...formData, last_name: e.target.value})} required className="rounded-none border-lab-border" data-testid="kontakt-nachname" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm text-lab-primary">E-Mail *</Label>
                  <Input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} required className="rounded-none border-lab-border" data-testid="kontakt-email-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm text-lab-primary">Unternehmen</Label>
                  <Input value={formData.company} onChange={e => setFormData({...formData, company: e.target.value})} className="rounded-none border-lab-border" data-testid="kontakt-firma" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm text-lab-primary">Nachricht *</Label>
                <Textarea value={formData.message} onChange={e => setFormData({...formData, message: e.target.value})} required rows={5} className="rounded-none border-lab-border resize-y" data-testid="kontakt-nachricht" placeholder="Beschreiben Sie kurz Ihr Anliegen..." />
              </div>
              <button type="submit" disabled={sending} className="bg-lab-primary text-white px-8 py-3 text-sm font-semibold hover:bg-lab-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2" data-testid="kontakt-submit">
                <Send className="w-4 h-4" /> {sending ? "Wird gesendet..." : "Nachricht senden"}
              </button>
            </form>
          </motion.div>
        </div>
      </main>

      <footer className="bg-lab-bg-dark border-t border-white/10">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-8 flex items-center justify-between">
          <p className="text-gray-500 text-sm">&copy; {new Date().getFullYear()} LAB 336</p>
          <Link to="/" className="text-gray-400 hover:text-lab-accent text-sm">Startseite</Link>
        </div>
      </footer>
    </div>
  );
}
